import { useLazyQuery, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  type SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem, GridRowParams, GridRowId } from "@mui/x-data-grid-premium";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
  PickersShortcutsProps,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExportDataButton } from "components/ExportDataButton";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { DueDateCell } from "components/Submission/DueDateCell";
import { FacilitiesCell } from "components/Submission/FacilitiesCell";
import { ALL_STATUSES, US_STATES } from "components/Submission/helpers/constants";
import { StaffAssigneeCell } from "components/Submission/StaffAssigneeCell";
import { SubmissionReviewerCell } from "components/Submission/SubmissionReviewerCell";
import { SubmissionStatusCell } from "components/Submission/SubmissionStatusCell";
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  RegulatoryStatusOption,
  SignatureType,
  SubmissionStatus,
  SubmissionType,
  SubmissionVirtualEventsQuery,
} from "generated-graphql/graphql";
import { useAutoFlex } from "hooks/useAutoFlex";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCreateStepperSubmission } from "hooks/useCreateStepperSubmission";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useOmnisearch } from "providers/OmnisearchDataContext";
import { OmnisearchProvider } from "providers/OmnisearchProvider";
import { SUBMISSION_VIRTUAL_EVENTS_QUERY } from "queries/submissions";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getDateLikeIsoDate } from "util/dates";

// Re-export Row type for backwards compatibility
import type { Row } from "./types";

import { metricsLogger } from "../../../../utils/metricsLogger";
import { BulkCreateNewSubmissionsDialog } from "./BulkCreateNewSubmissionsDialog";
import { CreateStepperArgs, CreateSubmissionTypeDialog } from "./CreateSubmissionTypeDialog";
import { GeneratorStatusFilter } from "./filters/GeneratorStatusFilter";
import { StateFilter } from "./filters/StateFilter";
import { StatusFilter } from "./filters/StatusFilter";
import { TagsFilter } from "./filters/TagsFilter";
import { GridSection } from "./GridSection";
import { getOmnisearchSessionKey, getOmnisearchStorageKey } from "./navigation";
import {
  WasteSubmissionsOverviewProvider,
  WasteSubmissionsOverviewFilter,
} from "./Overview/WasteSubmissionsOverviewContext";
import { SelectionProvider, useRowSelection, useChartSelection } from "./SelectionContext";
import { WasteSubmissionStatsProvider, useWasteSubmissionStats } from "./StatsContext";
import { StatsSection } from "./StatsSection";
import { SubmissionBulkActionsMenu } from "./SubmissionBulkActionsMenu";
import { SubmissionsEmptyState } from "./SubmissionsEmptyState";

// Stable empty array constant for use in memos to prevent reference changes
const EMPTY_ARRAY: string[] = [];

const ALL_GENERATOR_STATUSES = [
  RegulatoryStatusOption.LargeQuantityGenerator,
  RegulatoryStatusOption.SmallQuantityGenerator,
  RegulatoryStatusOption.VerySmallQuantityGenerator,
  RegulatoryStatusOption.None,
];

// Tags query for filtering
const FACILITY_TAGS_QUERY = gql(`
  query SubmissionsFacilityTags(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tags(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

// Date picker shortcuts
const datePickerShortcuts: PickersShortcutsProps<DateRange<DateTime>>["items"] = [
  {
    label: "This Quarter",
    getValue: () => [DateTime.now().startOf("quarter"), DateTime.now().endOf("quarter")],
  },
  {
    label: "Next Quarter",
    getValue: () => [
      DateTime.now().plus({ quarters: 1 }).startOf("quarter"),
      DateTime.now().plus({ quarters: 1 }).endOf("quarter"),
    ],
  },
  {
    label: "Next 90 Days",
    getValue: () => [DateTime.now(), DateTime.now().plus({ days: 90 })],
  },
  {
    label: "Next 6 Months",
    getValue: () => [DateTime.now(), DateTime.now().plus({ months: 6 })],
  },
  {
    label: "This Year",
    getValue: () => [DateTime.now().startOf("year"), DateTime.now().endOf("year")],
  },
  {
    label: "Last Year",
    getValue: () => [
      DateTime.now().minus({ years: 1 }).startOf("year"),
      DateTime.now().minus({ years: 1 }).endOf("year"),
    ],
  },
  {
    label: "Reset",
    getValue: () => [null, null],
  },
];

// Default = All statuses except Not Reporting
// Using explicit string values to ensure consistent comparison with Object.keys()
const DEFAULT_STATUSES: SubmissionStatus[] = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
  SubmissionStatus.FilingComplete,
];

// Default status values as strings for filtering - ensures exact match with Object.keys() output
const DEFAULT_STATUS_SET = new Set<string>(DEFAULT_STATUSES.map(String));

// Default date filter = 30 days back through 60 days forward (lookback keeps recently-past-due visible)
const DEFAULT_DUE_DATE_START = DateTime.now().minus({ days: 30 }).startOf("day").toISODate();
const DEFAULT_DUE_DATE_END = DateTime.now().plus({ days: 60 }).startOf("day").toISODate();
const DEFAULT_DUE_DATE = `${DEFAULT_DUE_DATE_START}..${DEFAULT_DUE_DATE_END}`;

// Default chart filter - ensures charts use the same defaults as the data grid
// This is a module-level constant since all values are derived from other constants
// IMPORTANT: Use UTC zone when parsing date strings to match how the datagrid resolver
// parses dates. Without this, local timezone interpretation causes events at midnight UTC
// to be excluded from charts but included in the datagrid.
const DEFAULT_CHART_FILTER: WasteSubmissionsOverviewFilter = {
  dueDateStart: DEFAULT_DUE_DATE_START
    ? (DateTime.fromISO(DEFAULT_DUE_DATE_START, { zone: "utc" }).toISO() ?? undefined)
    : undefined,
  dueDateEnd: DEFAULT_DUE_DATE_END
    ? (DateTime.fromISO(DEFAULT_DUE_DATE_END, { zone: "utc" }).toISO() ?? undefined)
    : undefined,
  statuses: DEFAULT_STATUSES,
};

// Helper to extract a key's value from an omnisearch string
function extractOmnisearchValue(omnisearch: string | null, key: string): string | undefined {
  if (!omnisearch) return undefined;
  const regex = new RegExp(`(?:^|\\s)${key}:(?:"([^"]+)"|([^\\s]+))`, "i");
  const match = omnisearch.match(regex);
  if (match) {
    return match[1] ?? match[2];
  }
  return undefined;
}

// Helper to compare arrays (order-independent)
function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

// Provider order is intentional:
// 1. WasteSubmissionStatsProvider - manages collapse state
// 2. SelectionProvider - isolates selection state to prevent full tree re-renders
// 3. WasteSubmissionsOmnisearchWrapper - SINGLE useOmnisearchDatagrid instance
//    shared by filter bar, charts, and grid via context. Eliminates the
//    dual-instance oscillation bug where two useOmnisearchInput hooks
//    fought over the URL.
// 4. WasteSubmissionsOverviewProvider - fires chart queries BEFORE DataGrid renders
export function WasteSubmissions() {
  return (
    <WasteSubmissionStatsProvider>
      <SelectionProvider>
        <WasteSubmissionsOmnisearchWrapper>
          <WasteSubmissionsOverviewProvider defaultFilter={DEFAULT_CHART_FILTER}>
            <WasteSubmissionsContent />
          </WasteSubmissionsOverviewProvider>
        </WasteSubmissionsOmnisearchWrapper>
      </SelectionProvider>
    </WasteSubmissionStatsProvider>
  );
}

function WasteSubmissionsOmnisearchWrapper({ children }: { children: React.ReactNode }) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isStaff } = useCurrentUser();

  const omnisearchPersistenceKey = "submissions-list-view-v9";
  const autoFlex = useAutoFlex(
    1.0,
    {
      deadline: 0.1,
      title: 0.25,
      facilities: 0.1,
      epaIds: 0.1,
      "submission.status": 0.1,
      submissionType: 0.1,
      "submission.title": 0.1,
    },
    [
      "facilities",
      "epaIds",
      "submissionType",
      "title",
      "documents",
      "reviewers",
      "signer",
      ...(isStaff ? ["staffAssignee"] : []),
      "submission.status",
      "submission.title",
    ]
  );

  const getRowHref = useCallback(
    (row: Row): string | null => {
      const submissionId = row.submission?.id;
      const isMaterializedInstance = row.frequency === "ONCE";

      if (!isMaterializedInstance) {
        if (!isStaff) {
          return null;
        }
        const dateOnly = DateTime.fromISO(String(row.deadline)).toISODate();
        return `/o/${tenantId}/waste/submissions/create?taskId=${row.taskId}&startDate=${dateOnly}`;
      }

      // Stepper submissions (data-entry 8700-12s) always open in the stepper,
      // for staff and customers alike, regardless of status.
      if (row.submission?.usesStepper) {
        return `/o/${tenantId}/waste/submissions/${submissionId}/stepper`;
      }

      if (!isStaff) {
        return `/o/${tenantId}/waste/submissions/${submissionId}`;
      }

      const status = row.submission?.status;
      const hasNoStaffReviewer = !row.submission?.reviewers?.some((r) => r.isStaff);
      const isUnassignedPeerReview =
        status === SubmissionStatus.EncampInitialReview &&
        row.submission?.peerReviewRequired === true &&
        hasNoStaffReviewer;

      if (isUnassignedPeerReview) {
        return `/o/${tenantId}/waste/submissions/${submissionId}/edit`;
      }

      const shouldViewReadOnly =
        status === SubmissionStatus.EncampInitialReview ||
        status === SubmissionStatus.InReview ||
        status === SubmissionStatus.FilingComplete ||
        status === SubmissionStatus.NotReporting;

      if (shouldViewReadOnly) {
        return `/o/${tenantId}/waste/submissions/${submissionId}`;
      }
      return `/o/${tenantId}/waste/submissions/${submissionId}/edit`;
    },
    [isStaff, tenantId]
  );

  const handleRowClick = useCallback(
    (row: Row) => {
      const href = getRowHref(row);
      if (href) {
        navigate(href);
      }
    },
    [getRowHref, navigate]
  );

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "deadline",
        headerName: "Due Date",
        sortable: true,
        flex: autoFlex.get("deadline"),
        minWidth: 20,
        renderCell: ({ row }) => {
          return <DueDateCell dueDate={row.submission?.dueDate} deadline={row.deadline} />;
        },
      },
      {
        field: "facilities",
        headerName: "Facilities",
        flex: autoFlex.get("facilities"),
        minWidth: 20,
        sortable: true,
        renderCell({ row }) {
          const facilities = row.facilities ?? [];
          return <FacilitiesCell facilities={facilities} />;
        },
      },
      {
        field: "epaIds",
        headerName: "EPA ID",
        flex: autoFlex.get("epaIds"),
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => {
          const epaIds = row.epaIds ?? [];
          if (epaIds.length === 0) {
            return "";
          }

          const epaIdsText = epaIds.join("; ");
          return (
            <Tooltip title={epaIdsText}>
              <CellText variant="body2" noWrap>
                {epaIdsText}
              </CellText>
            </Tooltip>
          );
        },
      },
      {
        field: "submission.submissionType",
        headerName: "Submission Type",
        flex: autoFlex.get("submission.submissionType"),
        minWidth: 100,
        sortable: true,
        renderCell: ({ row: { submission } }) => {
          return (
            <Tooltip title={submission?.submissionType}>
              <CellText variant="body2" noWrap>
                {submission?.submissionType ? prettyPrintEnumValue(submission.submissionType) : ""}
              </CellText>
            </Tooltip>
          );
        },
      },
      {
        field: "submission.title",
        headerName: "Submission Title",
        flex: autoFlex.get("submission.title"),
        minWidth: 100,
        sortable: true,
        renderCell: ({ row: { submission } }) => {
          return (
            <CellText variant="body2" noWrap>
              {submission?.title}
            </CellText>
          );
        },
      },

      {
        field: "notes",
        headerName: "Notes",
        flex: autoFlex.get("notes"),
        minWidth: 100,
        sortable: false,
        renderCell: ({ row }) => {
          const notes = row.submission?.notes;
          if (!notes) return "";
          return (
            <CellText variant="body2" noWrap>
              {notes}
            </CellText>
          );
        },
      },
      {
        field: "documents",
        headerName: "Attachment(s)",
        flex: autoFlex.get("documents"),
        minWidth: 60,
        sortable: false,
        renderCell: (params) => {
          const documents = params.row.submission?.documents || [];

          if (documents.length === 0) return "";
          const mappedDocuments = documents.map((doc) => doc.document.title).join(", ");
          return (
            <Tooltip title={mappedDocuments}>
              <CellText variant="body2">{mappedDocuments}</CellText>
            </Tooltip>
          );
        },
      },
      {
        field: "reviewers",
        headerName: "Reviewer(s)",
        flex: autoFlex.get("reviewers"),
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          const reviewers = params.row.submission?.reviewers || [];
          const reviewMode = params.row.submission?.reviewMode;
          const submissionStatus = params.row.submission?.status;

          if (reviewers.length === 0) {
            return null;
          }

          return (
            <SubmissionReviewerCell
              reviewers={reviewers}
              reviewMode={reviewMode}
              submissionStatus={submissionStatus}
              isStaff={isStaff}
              type="reviewer"
            />
          );
        },
      },
      {
        field: "signer",
        headerName: "Signer",
        flex: autoFlex.get("signer"),
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          const reviewers = params.row.submission?.reviewers || [];
          const reviewMode = params.row.submission?.reviewMode;
          const submissionStatus = params.row.submission?.status;
          const signers = reviewers.filter((r) => r.isSigner);
          const signatureType = params.row.submission?.signatureType;

          if (signatureType === SignatureType.None || signers.length === 0) {
            return (
              <CellText variant="body2" noWrap>
                No Signature Required
              </CellText>
            );
          }

          return (
            <SubmissionReviewerCell
              reviewers={reviewers}
              reviewMode={reviewMode}
              submissionStatus={submissionStatus}
              isStaff={isStaff}
              type="signer"
            />
          );
        },
      },
      ...(isStaff
        ? [
            {
              field: "staffAssignee",
              headerName: "Encamp Assignee",
              flex: autoFlex.get("staffAssignee"),
              minWidth: 90,
              renderCell: ({ row }: { row: Row }) => {
                return <StaffAssigneeCell staffAssignee={row.submission?.staffAssignee} />;
              },
            },
          ]
        : []),
      {
        field: "submission.status",
        headerName: "Status",
        flex: autoFlex.get("submission.status"),
        minWidth: isStaff ? 180 : 120,
        sortable: true,
        renderCell({ row: { submission } }) {
          return (
            <SubmissionStatusCell
              status={submission?.status}
              encampStatus={submission?.encampStatus}
              isStaff={isStaff}
            />
          );
        },
      },
      {
        field: "waitingOn",
        headerName: "Waiting On",
        flex: autoFlex.get("waitingOn"),
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          const waitingOn = params.row.waitingOn || [];

          if (waitingOn.length === 0) {
            return null;
          }

          return (
            <Stack
              spacing={0}
              style={{
                paddingTop: theme.spacing(1),
                paddingBottom: theme.spacing(1),
                height: "100%",
                width: "100%",
                alignItems: "start",
                overflow: "hidden",
              }}
            >
              {waitingOn.map((user) => {
                const displayName =
                  user.person?.first || user.person?.last
                    ? `${user.person.first || ""} ${user.person.last || ""}`.trim()
                    : user.email;

                return (
                  <Typography
                    key={user.id || user.email}
                    variant="body2"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "inherit",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Tooltip title={user.email}>
                      <span>{displayName}</span>
                    </Tooltip>
                  </Typography>
                );
              })}
            </Stack>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        sortable: false,
        align: "right",
        getActions: ({ row }) => {
          const actions: any[] = [];
          const isMaterializedInstance = row.frequency === "ONCE";
          if (!isMaterializedInstance && isStaff) {
            actions.push(
              <Tooltip title="Create Submission" key="createSubmission">
                <GridActionsCellItem
                  onClick={() => handleRowClick(row)}
                  label="Create Submission"
                  icon={<Add />}
                />
              </Tooltip>
            );
          }

          return actions;
        },
      },
    ],
    [autoFlex, theme, handleRowClick, isStaff]
  );

  const additionalFilterColumns = useMemo<FilterKey[]>(
    () => [
      {
        header: "Facility ID",
        key: "customerFacilityId",
        filterKeyType: "text",
      },
      {
        header: "EPA ID",
        key: "epaId",
        filterKeyType: "text",
      },
      {
        header: "Facility",
        key: "facilityId",
        filterKeyType: "wasteMultipleFacility",
      },
      {
        header: "Attachment Name",
        key: "documents",
        filterKeyType: "text",
      },
      {
        header: "Submission Type",
        key: "submissionType",
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(SubmissionType),
      },
      {
        header: "Reviewer",
        key: "reviewers",
        filterKeyType: "text",
        nullCheckboxLabel: "No Reviewer",
      },
      {
        header: "Signer",
        key: "signer",
        filterKeyType: "text",
        nullCheckboxLabel: "No Signer",
      },
      ...(isStaff
        ? [
            {
              header: "Encamp Assignee",
              key: "staffAssignee",
              filterKeyType: "text" as const,
            },
          ]
        : []),
      {
        header: "Waiting On",
        key: "waitingOn",
        filterKeyType: "multipleUser",
      },
      {
        header: "Is Revision",
        key: "isRevision",
        filterKeyType: "boolean",
      },
    ],
    [isStaff]
  );

  const excludeFilterColumns = useMemo(
    () => [
      "submissionType",
      "dueDate",
      "status",
      "documents",
      "reviewers",
      "signer",
      ...(isStaff ? ["staffAssignee"] : []),
      "facilityName",
      "submission.submissionType",
      "facilityId",
      "deadline",
      "facilities",
      "epaIds",
      "submission.status",
      "waitingOn",
    ],
    [isStaff]
  );

  return (
    <OmnisearchProvider
      persistenceKey={omnisearchPersistenceKey}
      columns={columns}
      isURLDriven
      initialSortModel={[
        { field: "dueDate", sort: "asc" },
        { field: "submission.submissionType", sort: "asc" },
      ]}
      initialPageSize={50}
      initialColumnVisibilityModel={{
        notes: false,
        waitingOn: false,
      }}
      additionalFilters={additionalFilterColumns}
      excludeFilters={excludeFilterColumns}
      isMobile={isMobile}
    >
      {children}
    </OmnisearchProvider>
  );
}

function WasteSubmissionsContent() {
  const { tenantId: tenantIdFromParams } = useParams();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { isStaff, user } = useCurrentUser();
  const currentUserEmail = user?.email;
  const { statsOpen, handleToggle } = useWasteSubmissionStats();

  // Selection state from context - prevents full tree re-renders on selection changes
  const { selectedRows, selectedIds, setSelectedRows, setSelectedIds, clearSelections } =
    useRowSelection();
  const {
    selectedChartStatus,
    selectedChartState,
    selectedChartGeneratorStatus,
    setSelectedChartStatus,
    setSelectedChartState,
    setSelectedChartGeneratorStatus,
  } = useChartSelection();

  // Tags filter state - tags list for filter component
  const [allFacilityTags, setAllFacilityTags] = useState<Array<{ id: string; name: string }>>([]);
  const [facilityTagsPage, setFacilityTagsPage] = useState(0);
  const [isFetchingMoreFacilityTags, setIsFetchingMoreFacilityTags] = useState(false);

  // Fetch facility tags - use regular useQuery to avoid suspending the whole component
  // Always filter out internal tags on this page (for both staff and customers)
  const { data: tagsData } = useQuery(FACILITY_TAGS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} isWasteFacilityTag:true`,
      page: 0,
      pageSize: 100,
      sort: [{ field: "name", sort: "asc" }],
    },
    skip: !tenantId,
  });

  // Lazy query for loading more tags
  const [fetchMoreFacilityTags, { data: moreTagsData, error: moreTagsError }] =
    useLazyQuery(FACILITY_TAGS_QUERY);

  // Handle lazy query completion (onCompleted is deprecated in Apollo Client 3.14+)
  useEffect(() => {
    if (moreTagsData?.tags?.items) {
      setAllFacilityTags((prev) => [...prev, ...moreTagsData.tags.items]);
      setIsFetchingMoreFacilityTags(false);
    }
  }, [moreTagsData]);

  // Handle lazy query error (onError is deprecated in Apollo Client 3.14+)
  useEffect(() => {
    if (moreTagsError) {
      setIsFetchingMoreFacilityTags(false);
    }
  }, [moreTagsError]);

  // Initialize tags list with initial data
  useEffect(() => {
    if (tagsData?.tags?.items) {
      setAllFacilityTags(tagsData.tags.items);
    }
  }, [tagsData]);

  const hasMoreFacilityTags = useMemo(() => {
    if (!tagsData?.tags?.count) return false;
    return allFacilityTags.length < tagsData.tags.count;
  }, [tagsData?.tags?.count, allFacilityTags.length]);

  const handleFacilityTagsScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const scrollThreshold = 50;
      const scrollDistance = target.scrollHeight - target.scrollTop - target.clientHeight;

      if (scrollDistance < scrollThreshold && hasMoreFacilityTags && !isFetchingMoreFacilityTags) {
        setIsFetchingMoreFacilityTags(true);
        const nextPage = facilityTagsPage + 1;
        setFacilityTagsPage(nextPage);

        fetchMoreFacilityTags({
          variables: {
            search: `tenantId:${tenantId} isWasteFacilityTag:true`,
            page: nextPage,
            pageSize: 100,
            sort: [{ field: "name", sort: "asc" }],
          },
        });
      }
    },
    [
      hasMoreFacilityTags,
      isFetchingMoreFacilityTags,
      facilityTagsPage,
      tenantId,
      fetchMoreFacilityTags,
    ]
  );

  const getRowHref = useCallback(
    (row: Row): string | null => {
      const submissionId = row.submission?.id;

      // Virtual events come from recurring tasks (YEARLY, MONTHLY, etc.)
      // Materialized instances have frequency ONCE and their own submission record
      const isMaterializedInstance = row.frequency === "ONCE";

      if (!isMaterializedInstance) {
        if (!isStaff) {
          return null;
        }
        const dateOnly = getDateLikeIsoDate(String(row.deadline));
        if (!dateOnly) {
          return null;
        }
        return `/o/${tenantId}/waste/submissions/create?taskId=${row.taskId}&startDate=${dateOnly}`;
      }

      // Stepper submissions (data-entry 8700-12s) always open in the stepper,
      // for staff and customers alike, regardless of status.
      if (row.submission?.usesStepper) {
        return `/o/${tenantId}/waste/submissions/${submissionId}/stepper`;
      }

      if (!isStaff) {
        return `/o/${tenantId}/waste/submissions/${submissionId}`;
      }

      // Staff users: determine view mode based on submission status
      const status = row.submission?.status;
      const hasNoStaffReviewer = !row.submission?.reviewers?.some((r) => r.isStaff);
      const isUnassignedPeerReview =
        status === SubmissionStatus.EncampInitialReview &&
        row.submission?.peerReviewRequired === true &&
        hasNoStaffReviewer;

      if (isUnassignedPeerReview) {
        return `/o/${tenantId}/waste/submissions/${submissionId}/edit`;
      }

      const shouldViewReadOnly =
        status === SubmissionStatus.EncampInitialReview ||
        status === SubmissionStatus.InReview ||
        status === SubmissionStatus.FilingComplete ||
        status === SubmissionStatus.NotReporting;

      if (shouldViewReadOnly) {
        return `/o/${tenantId}/waste/submissions/${submissionId}`;
      }
      return `/o/${tenantId}/waste/submissions/${submissionId}/edit`;
    },
    [isStaff, tenantId]
  );

  const handleRowClick = useCallback(
    (row: Row) => {
      const href = getRowHref(row);
      if (href) {
        navigate(href);
      }
    },
    [getRowHref, navigate]
  );

  const alerts = useAlerts();
  const { featureFlags } = useFeatureFlags();
  const stepperEnabled = featureFlags[FeatureFlagKey.WasteSubmissionStepper] === true;
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const { createStepperSubmission, loading: creatingStepper } = useCreateStepperSubmission();

  // With the stepper enabled, "Create Submission" first asks which flow to
  // use (the 8700-12 stepper or document review for everything else). Without
  // it, keep the existing behavior of going straight to the create form.
  const handleAddSubmission = useCallback(() => {
    if (stepperEnabled) {
      setShowTypeDialog(true);
      return;
    }
    navigate(`/o/${tenantId}/waste/submissions/create`);
  }, [stepperEnabled, navigate, tenantId]);

  // Document-review path: all fields (type, method, title, ...) are entered on
  // the create form itself, so the dialog forwards nothing.
  const handleContinueLegacy = useCallback(() => {
    setShowTypeDialog(false);
    navigate(`/o/${tenantId}/waste/submissions/create`);
  }, [navigate, tenantId]);

  // Stepper path: create the data-entry submission directly and drop the user
  // into the stepper.
  const handleCreateStepper = useCallback(
    async (args: CreateStepperArgs) => {
      try {
        const id = await createStepperSubmission(args);
        if (id) {
          setShowTypeDialog(false);
          navigate(`/o/${tenantId}/waste/submissions/${id}/stepper`);
        }
      } catch (error) {
        alerts.error("Failed to create the submission.", error);
      }
    },
    [createStepperSubmission, navigate, tenantId, alerts]
  );

  const [showBulkCreateDialog, setShowBulkCreateDialog] = useState(false);

  const handleOpenBulkCreate = useCallback(() => {
    setShowBulkCreateDialog(true);
  }, []);

  const handleBulkCreateSuccess = useCallback(() => {
    clearSelections();
  }, [clearSelections]);

  useBreadcrumb([{ label: "Waste", to: `/o/${tenantId}/waste` }, { label: "Submissions" }]);

  const omnisearchPersistenceKey = "submissions-list-view-v9";

  // Omnisearch state comes from OmnisearchProvider via context
  // (single instance shared by filter bar, charts, and grid)
  const {
    omnisearch,
    setOmnisearch,
    setOmnisearchImmediate,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  } = useOmnisearch();

  const [searchParams] = useSearchParams();
  const hasRestoredOmnisearch = useRef(false);

  // On mount, if no URL omnisearch param, restore last-used filters from localStorage.
  // NOTE: This page does NOT use the usePersistedFilters hook because the non-omnisearch
  // filters (date range, status, etc.) are driven in part by the omnisearch/table filters.
  // Persisting them separately would interfere with omnisearch defaults. New pages should
  // prefer the usePersistedFilters hook instead.
  useEffect(() => {
    if (hasRestoredOmnisearch.current) return;

    if (searchParams.has("omnisearch") || omnisearch) {
      hasRestoredOmnisearch.current = true;
      return;
    }

    if (!tenantId || !currentUserEmail) return;
    hasRestoredOmnisearch.current = true;
    const saved = localStorage.getItem(getOmnisearchStorageKey(tenantId, currentUserEmail));
    if (saved) {
      setOmnisearchImmediate(saved);
    }
  }, [searchParams, omnisearch, setOmnisearchImmediate, tenantId, currentUserEmail]);

  // Save omnisearch to sessionStorage (back navigation) and localStorage (cross-session persistence).
  // tenantId and email are read from refs so that a tenant switch doesn't
  // re-trigger this effect and write stale filters to the new tenant's key.
  const tenantIdRef = useRef(tenantId);
  const currentUserEmailRef = useRef(currentUserEmail);
  tenantIdRef.current = tenantId;
  currentUserEmailRef.current = currentUserEmail;
  useEffect(() => {
    const tid = tenantIdRef.current;
    const email = currentUserEmailRef.current;
    if (!tid || !email) return;
    const sessionKey = getOmnisearchSessionKey(tid, email);
    const storageKey = getOmnisearchStorageKey(tid, email);
    if (omnisearch) {
      sessionStorage.setItem(sessionKey, omnisearch);
      localStorage.setItem(storageKey, omnisearch);
    } else {
      sessionStorage.removeItem(sessionKey);
      localStorage.removeItem(storageKey);
    }
  }, [omnisearch]);

  // Facility tags filter state - derived from omnisearch (syncs to grid query)
  // Uses tag NAMES for search (like facilityTags:"California Sites")
  // Also supports facilityTags:null to filter for facilities with no tags
  // Use extractKeyValuePairs to properly handle quoted values with pipes
  const facilityTagsFilterValue = useMemo(() => {
    if (!omnisearch) return { tagNames: [], isNull: false };
    const pairs = extractKeyValuePairs(omnisearch);
    const tagsPair = pairs.find((p) => p.key === "facilityTags");
    if (!tagsPair || !tagsPair.value) return { tagNames: [], isNull: false };

    // Check if filtering for facilities with no tags
    if (tagsPair.value.length === 1 && tagsPair.value[0].toLowerCase() === "null") {
      return { tagNames: [], isNull: true };
    }

    // extractKeyValuePairs returns an array of values, properly handling quotes
    return {
      tagNames: tagsPair.value.filter((s) => s.length > 0),
      isNull: false,
    };
  }, [omnisearch]);

  const committedFacilityTagNames = facilityTagsFilterValue.tagNames;
  const isFacilityTagsNull = facilityTagsFilterValue.isNull;

  // Callback for TagsFilter component to commit changes to omnisearch
  const handleTagsFilterCommit = useCallback(
    (tagNames: string[]) => {
      if (tagNames.length === 0) {
        // None selected = remove from omnisearch
        removeOmnisearchKeyValue("facilityTags");
      } else {
        // Quote tag names that contain spaces, use pipes as separator
        const tagNamesString = tagNames
          .map((name) => (name.includes(" ") ? `"${name}"` : name))
          .join("|");
        editOmnisearchKeyValues([["facilityTags", tagNamesString]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Check if tag filter is at default (none selected)
  const isTagFilterAtDefault = committedFacilityTagNames.length === 0;

  type ViewByValue = "all" | "readyForAction" | "needsMyAttention" | "peerReviewQueue";

  // Status filter state - derived from omnisearch
  const statusFilterValue = extractOmnisearchValue(omnisearch, "submissionStatus");
  // Helper to get default statuses from ALL_STATUSES for consistent value format
  const defaultStatusesFromAll = useMemo(
    () => ALL_STATUSES.filter((s) => DEFAULT_STATUS_SET.has(s)),
    []
  );

  const committedStatuses = useMemo(() => {
    // No filter in omnisearch = use default statuses (excludes completed)
    // Return constant directly to maintain stable reference
    if (statusFilterValue === undefined || statusFilterValue === "") {
      return defaultStatusesFromAll;
    }
    // "none" marker = no statuses selected (show nothing)
    if (statusFilterValue === "none") {
      return EMPTY_ARRAY;
    }
    // Support both pipe (|) and comma (,) separators for compatibility with data grid filters
    // Filter out invalid status values to prevent GraphQL enum validation errors
    const parsedStatuses = statusFilterValue
      .split(/[|,]/)
      .filter((s) => s.length > 0 && ALL_STATUSES.includes(s as SubmissionStatus));
    // If all values were invalid, fall back to defaults
    return parsedStatuses.length > 0 ? parsedStatuses : defaultStatusesFromAll;
  }, [statusFilterValue, defaultStatusesFromAll]);

  // Callback for StatusFilter component to commit changes to omnisearch
  const handleStatusFilterCommit = useCallback(
    (statuses: string[]) => {
      if (statuses.length === 1 && statuses[0] === "none") {
        // No statuses selected
        editOmnisearchKeyValues([["submissionStatus", "none"]]);
      } else if (statuses.length === 0) {
        // Matches defaults - remove from omnisearch to let dynamic default apply
        removeOmnisearchKeyValue("submissionStatus");
      } else {
        // Custom selection
        editOmnisearchKeyValues([["submissionStatus", statuses.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Check if status filter is at default (based on committed values)
  const isStatusFilterAtDefault = useMemo(() => {
    if (committedStatuses.length !== defaultStatusesFromAll.length) return false;
    return defaultStatusesFromAll.every((s) => committedStatuses.includes(s));
  }, [committedStatuses, defaultStatusesFromAll]);

  // Extract readyForAction email value from omnisearch
  const readyForActionEmail = extractOmnisearchValue(omnisearch, "readyForAction");
  const readyForAction = !!readyForActionEmail;
  const filedSubmissionsDigest = extractOmnisearchValue(omnisearch, "filedSubmissionsDigest");
  const isFiledSubmissionsDigest = filedSubmissionsDigest === "true";

  // Staff-only filter state
  const needsMyAttentionValue = extractOmnisearchValue(omnisearch, "needsMyAttention");
  const needsMyAttention = needsMyAttentionValue === "true";
  const peerReviewQueueValue = extractOmnisearchValue(omnisearch, "peerReviewQueue");
  const peerReviewQueue = peerReviewQueueValue === "true";

  const viewByValue = useMemo((): ViewByValue => {
    if (readyForAction) return "readyForAction";
    if (needsMyAttention) return "needsMyAttention";
    if (peerReviewQueue) return "peerReviewQueue";
    return "all";
  }, [readyForAction, needsMyAttention, peerReviewQueue]);

  const handleViewByChange = useCallback(
    (event: SelectChangeEvent<ViewByValue>) => {
      const value = event.target.value as ViewByValue;

      let cleaned = omnisearch
        .replace(/\b(readyForAction|needsMyAttention|peerReviewQueue):\S+/g, "")
        .replace(/\s+/g, " ")
        .trim();

      switch (value) {
        case "readyForAction":
          if (currentUserEmail) {
            cleaned = `${cleaned} readyForAction:${currentUserEmail}`.trim();
          }
          break;
        case "needsMyAttention":
          cleaned = `${cleaned} needsMyAttention:true`.trim();
          break;
        case "peerReviewQueue":
          cleaned = `${cleaned} peerReviewQueue:true`.trim();
          break;
      }

      setOmnisearchImmediate(cleaned);
      setSelectedChartStatus(null);
    },
    [omnisearch, currentUserEmail, setOmnisearchImmediate, setSelectedChartStatus]
  );

  // Static defaultSearch - always includes defaults
  // The omnisearch backend merges duplicate keys, with later values taking precedence
  // So if user sets submissionStatus:X in omnisearch, it overrides the default here
  // Note: status:ACTIVE|OVERDUE was removed because Task.completedAt is not automatically
  // set by the submission pipeline, making it redundant with submissionStatus filtering
  const defaultSearch = useMemo(() => {
    if (isFiledSubmissionsDigest) {
      return `tenantId:${tenantId}`;
    }

    return `tenantId:${tenantId} submissionStatus:${defaultStatusesFromAll.join(
      "|"
    )} dueDate:${DEFAULT_DUE_DATE}`;
  }, [tenantId, defaultStatusesFromAll, isFiledSubmissionsDigest]);

  const handleViewUpcoming = useCallback(() => {
    editOmnisearchKeyValues([["submissionStatus", SubmissionStatus.NotStarted]]);
  }, [editOmnisearchKeyValues]);

  const clearOmnisearch = useCallback(() => {
    metricsLogger.startInteraction("wasteSubmissions.clearOmnisearch");
    setOmnisearchImmediate("");
  }, [setOmnisearchImmediate]);

  // Memoized callbacks for OmnisearchDataGrid to prevent unnecessary re-renders
  const getItems = useCallback(
    (data: SubmissionVirtualEventsQuery) => data.submissionVirtualEvents.items,
    []
  );

  const getCount = useCallback(
    (data: SubmissionVirtualEventsQuery) => data.submissionVirtualEvents.count ?? 0,
    []
  );

  const handleSelectedRowsChanged = useCallback(
    (rows: Row[]) => setSelectedRows(rows),
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    []
  );

  const handleSelectedIdsChanged = useCallback(
    (ids: GridRowId[]) => setSelectedIds(ids as string[]),
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    []
  );

  const handleGridRowClick = useCallback(
    ({ row }: GridRowParams<Row>) => handleRowClick(row),
    [handleRowClick]
  );

  // Memoized clear selections callback for bulk actions - uses context's clearSelections
  const handleClearSelections = useCallback(() => {
    clearSelections();
  }, [clearSelections]);

  // Memoize columnVisibilityModel
  const columnVisibilityModel = useMemo(
    () => ({
      notes: false,
      waitingOn: false,
      ...(isMobile && {
        facilities: false,
        tags: false,
      }),
    }),
    [isMobile]
  );

  // Memoize commandButtons array
  const commandButtons = useMemo(
    () => [
      <ExportDataButton
        key="export"
        fileName={`waste-submissions-${tenantId}`}
        tenantId={tenantId}
        filter={{ search: omnisearch || `tenantId:${tenantId}` }}
        exportType="wasteSubmissions"
        dialog={true}
      />,
      ...(isStaff
        ? [
            <Button
              key="create-submission"
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddSubmission}
              color="secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              Create Submission
            </Button>,
            <Button
              key="bulk-create-submissions"
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenBulkCreate}
              color="secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              Bulk Create
            </Button>,
            <SubmissionBulkActionsMenu
              key="bulk-actions"
              submissions={selectedRows}
              onClearSelections={handleClearSelections}
            />,
          ]
        : []),
    ],
    [
      tenantId,
      omnisearch,
      isStaff,
      selectedRows,
      handleClearSelections,
      handleAddSubmission,
      handleOpenBulkCreate,
    ]
  );

  // Handler for clicking on a status in the chart
  // Uses functional update to avoid dependency on selectedChartStatus
  const handleChartStatusClick = useCallback(
    (status: SubmissionStatus) => {
      metricsLogger.startInteraction("wasteSubmissions.chartStatusClick");
      setSelectedChartStatus((prev) => {
        // If clicking the same status, clear the filter
        if (prev === status) {
          removeOmnisearchKeyValue("submissionStatus");
          return null;
        } else {
          editOmnisearchKeyValues([["submissionStatus", status]]);
          return status;
        }
      });
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, setSelectedChartStatus]
  );

  // Handler for clicking on a state in the chart
  // Uses functional update to avoid dependency on selectedChartState
  const handleChartStateClick = useCallback(
    (state: string) => {
      metricsLogger.startInteraction("wasteSubmissions.chartStateClick");
      setSelectedChartState((prev) => {
        // If clicking the same state, clear the filter
        if (prev === state) {
          removeOmnisearchKeyValue("states");
          return null;
        } else {
          editOmnisearchKeyValues([["states", state]]);
          return state;
        }
      });
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, setSelectedChartState]
  );

  // Handler for clicking on a generator status in the chart
  // Uses functional update to avoid dependency on selectedChartGeneratorStatus
  const handleChartGeneratorStatusClick = useCallback(
    (status: string) => {
      metricsLogger.startInteraction("wasteSubmissions.chartGeneratorStatusClick");
      setSelectedChartGeneratorStatus((prev) => {
        // If clicking the same status, clear the filter
        if (prev === status) {
          removeOmnisearchKeyValue("generatorStatus");
          return null;
        } else {
          editOmnisearchKeyValues([["generatorStatus", status]]);
          return status;
        }
      });
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, setSelectedChartGeneratorStatus]
  );

  // Handler for resetting all filters to default
  const handleResetFilters = useCallback(() => {
    metricsLogger.startInteraction("wasteSubmissions.resetFilters");
    // Clear all filters from omnisearch at once - using setOmnisearch directly
    // to avoid stale state issues with multiple removeOmnisearchKeyValue calls
    const keysToRemove = new Set([
      "submissionStatus",
      "dueDate",
      "states",
      "generatorStatus",
      "epaId",
      "readyForAction",
      "needsMyAttention",
      "peerReviewQueue",
      "facilityTags",
      "isRevision",
    ]);

    // Parse omnisearch tokens respecting quotes
    const tokens: string[] = [];
    let current = "";
    let inQuotes = false;
    let quoteChar = "";
    for (let i = 0; i < omnisearch.length; i++) {
      const char = omnisearch[i];
      if (!inQuotes && (char === '"' || char === "'")) {
        inQuotes = true;
        quoteChar = char;
        current += char;
      } else if (inQuotes && char === quoteChar) {
        inQuotes = false;
        quoteChar = "";
        current += char;
      } else if (!inQuotes && char === " ") {
        if (current.trim()) {
          tokens.push(current.trim());
          current = "";
        }
      } else {
        current += char;
      }
    }
    if (current.trim()) tokens.push(current.trim());

    // Filter out tokens with keys to remove
    const filteredTokens = tokens.filter((token) => {
      const colonIndex = token.indexOf(":");
      if (colonIndex > 0) {
        const key = token.substring(0, colonIndex);
        return !keysToRemove.has(key);
      }
      return true; // Keep non-key-value tokens
    });

    setOmnisearch(filteredTokens.join(" "));

    // Reset chart selections
    setSelectedChartStatus(null);
    setSelectedChartState(null);
    setSelectedChartGeneratorStatus(null);
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [omnisearch, setOmnisearch]);

  // Derive timeFilter equivalent for empty state component
  const timeFilterForEmptyState = useMemo(() => {
    if (committedStatuses.length === 1) {
      if (committedStatuses[0] === SubmissionStatus.NotStarted) return "upcoming";
      if (
        committedStatuses[0] === SubmissionStatus.FilingComplete ||
        committedStatuses[0] === SubmissionStatus.NotReporting
      )
        return "completed";
    }
    // Check if it's the "in-progress" group
    const inProgressStatuses = [
      SubmissionStatus.InProgress,
      SubmissionStatus.EncampInitialReview,
      SubmissionStatus.InReview,
      SubmissionStatus.ProcessingSubmission,
    ];
    if (
      committedStatuses.length > 0 &&
      committedStatuses.every((s) => inProgressStatuses.includes(s as SubmissionStatus))
    ) {
      return "in-progress";
    }
    return "";
  }, [committedStatuses]);

  // State filter state - derived from omnisearch
  const statesFilterValue = extractOmnisearchValue(omnisearch, "states");

  const committedStates = useMemo(() => {
    // No filter = all states selected
    // Return constant directly to maintain stable reference
    if (statesFilterValue === undefined || statesFilterValue === "") {
      return US_STATES;
    }
    // "none" marker = no states selected
    if (statesFilterValue === "none") {
      return EMPTY_ARRAY;
    }
    // Support both pipe (|) and comma (,) separators for compatibility with data grid filters
    return statesFilterValue.split(/[|,]/).filter((s) => s.length > 0);
  }, [statesFilterValue]);

  // Callback for StateFilter component to commit changes to omnisearch
  const handleStateFilterCommit = useCallback(
    (states: string[]) => {
      if (states.length === 0) {
        // None selected
        editOmnisearchKeyValues([["states", "none"]]);
      } else if (states.length === US_STATES.length) {
        // All selected = remove from omnisearch to let default apply
        removeOmnisearchKeyValue("states");
      } else {
        // Custom selection
        editOmnisearchKeyValues([["states", states.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Check if state filter is at default (all states selected)
  const isStateFilterAtDefault = committedStates.length === US_STATES.length;

  // Generator status filter state - derived from omnisearch
  const generatorStatusFilterValue = extractOmnisearchValue(omnisearch, "generatorStatus");
  const committedGeneratorStatuses = useMemo(() => {
    // No filter = all generator statuses selected
    // Return constant directly to maintain stable reference
    if (generatorStatusFilterValue === undefined || generatorStatusFilterValue === "") {
      return ALL_GENERATOR_STATUSES;
    }
    // "none" marker (legacy) = treat as selecting only NONE generator status
    // This prevents errors from sending invalid enum value to backend
    if (generatorStatusFilterValue === "none") {
      return [RegulatoryStatusOption.None];
    }
    // Support both pipe (|) and comma (,) separators for compatibility with data grid filters
    // Filter out invalid generator status values to prevent GraphQL enum validation errors
    const parsedStatuses = generatorStatusFilterValue
      .split(/[|,]/)
      .filter(
        (s) => s.length > 0 && ALL_GENERATOR_STATUSES.includes(s as RegulatoryStatusOption)
      ) as RegulatoryStatusOption[];
    // If all values were invalid, fall back to defaults
    return parsedStatuses.length > 0 ? parsedStatuses : ALL_GENERATOR_STATUSES;
  }, [generatorStatusFilterValue]);

  // Callback for GeneratorStatusFilter component to commit changes to omnisearch
  const handleGeneratorStatusFilterCommit = useCallback(
    (statuses: RegulatoryStatusOption[]) => {
      if (statuses.length === 0) {
        // None selected = treat as selecting only NONE generator status
        editOmnisearchKeyValues([["generatorStatus", RegulatoryStatusOption.None]]);
      } else if (statuses.length === ALL_GENERATOR_STATUSES.length) {
        // All selected = remove from omnisearch to let default apply
        removeOmnisearchKeyValue("generatorStatus");
      } else {
        // Custom selection
        editOmnisearchKeyValues([["generatorStatus", statuses.join("|")]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Check if generator status filter is at default (all selected)
  const isGeneratorStatusFilterAtDefault =
    committedGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length;

  // Date range filter state - derived from omnisearch hook state
  // IMPORTANT: Use UTC zone when parsing date-only strings to match how the backend
  // resolver parses dates. Without this, Luxon interprets date-only strings in local
  // timezone, causing events at midnight UTC to be counted differently by the charts
  // vs the datagrid.
  const dueDateValue = extractOmnisearchValue(omnisearch, "dueDate");
  const [dueDateStart, dueDateEnd] = useMemo(() => {
    // No filter in omnisearch = use default (30 days back through 60 days forward)
    if (!dueDateValue) {
      return [
        DateTime.fromISO(DEFAULT_DUE_DATE_START, { zone: "utc" }),
        DateTime.fromISO(DEFAULT_DUE_DATE_END, { zone: "utc" }),
      ];
    }
    const parts = dueDateValue.split("..");
    const start = parts[0] ? DateTime.fromISO(parts[0], { zone: "utc" }) : null;
    const end = parts[1] ? DateTime.fromISO(parts[1], { zone: "utc" }) : null;
    return [start, end];
  }, [dueDateValue]);

  // Check if due date filter is at default (30 days back through 60 days forward)
  const isDueDateFilterAtDefault = useMemo(() => {
    if (!dueDateStart || !dueDateEnd) return false;
    const defaultStart = DateTime.fromISO(DEFAULT_DUE_DATE_START, {
      zone: "utc",
    });
    const defaultEnd = DateTime.fromISO(DEFAULT_DUE_DATE_END, { zone: "utc" });
    return (
      dueDateStart.toISODate() === defaultStart.toISODate() &&
      dueDateEnd.toISODate() === defaultEnd.toISODate()
    );
  }, [dueDateStart, dueDateEnd]);

  // Check if ALL filters are at their default values
  const areFiltersAtDefault = useMemo(() => {
    return (
      isStatusFilterAtDefault &&
      isStateFilterAtDefault &&
      isGeneratorStatusFilterAtDefault &&
      isTagFilterAtDefault &&
      isDueDateFilterAtDefault &&
      !readyForAction &&
      !needsMyAttention &&
      !peerReviewQueue
    );
  }, [
    isStatusFilterAtDefault,
    isStateFilterAtDefault,
    isGeneratorStatusFilterAtDefault,
    isTagFilterAtDefault,
    isDueDateFilterAtDefault,
    readyForAction,
    needsMyAttention,
    peerReviewQueue,
  ]);

  const handleDueDateRangeChange = useCallback(
    (newValue: DateRange<DateTime>) => {
      const [start, end] = newValue;
      if (!start && !end) {
        // Cleared - remove from omnisearch (dynamic default will apply)
        removeOmnisearchKeyValue("dueDate");
      } else {
        const startStr = start?.toISODate() ?? "";
        const endStr = end?.toISODate() ?? "";
        const newDateRange = `${startStr}..${endStr}`;
        // If selecting exactly default date range, remove from omnisearch
        if (newDateRange === DEFAULT_DUE_DATE) {
          removeOmnisearchKeyValue("dueDate");
        } else {
          editOmnisearchKeyValues([["dueDate", newDateRange]]);
        }
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Clear bulk selections when filtering changes
  useEffect(() => {
    clearSelections();
  }, [omnisearch, clearSelections]);

  // Sync chart selection states with omnisearch filter values
  // This handles the case where filters are changed externally (not via chart click)
  useEffect(() => {
    // If status filter doesn't match the chart selection, clear the chart selection
    if (selectedChartStatus) {
      const isStatusInFilter = statusFilterValue?.includes(selectedChartStatus);
      const isSingleStatusFilter =
        statusFilterValue && !statusFilterValue.includes("|") && !statusFilterValue.includes(",");
      // Only keep selection if it's a single-value filter matching the selection
      if (!isStatusInFilter || !isSingleStatusFilter || statusFilterValue !== selectedChartStatus) {
        setSelectedChartStatus(null);
      }
    }
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [statusFilterValue, selectedChartStatus]);

  useEffect(() => {
    // If states filter doesn't match the chart selection, clear the chart selection
    if (selectedChartState) {
      const isStateInFilter = statesFilterValue?.includes(selectedChartState);
      const isSingleStateFilter =
        statesFilterValue && !statesFilterValue.includes("|") && !statesFilterValue.includes(",");
      // Only keep selection if it's a single-value filter matching the selection
      if (!isStateInFilter || !isSingleStateFilter || statesFilterValue !== selectedChartState) {
        setSelectedChartState(null);
      }
    }
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [statesFilterValue, selectedChartState]);

  useEffect(() => {
    // If generator status filter doesn't match the chart selection, clear the chart selection
    if (selectedChartGeneratorStatus) {
      const isGenStatusInFilter = generatorStatusFilterValue?.includes(
        selectedChartGeneratorStatus
      );
      const isSingleGenStatusFilter =
        generatorStatusFilterValue &&
        !generatorStatusFilterValue.includes("|") &&
        !generatorStatusFilterValue.includes(",");
      // Only keep selection if it's a single-value filter matching the selection
      if (
        !isGenStatusInFilter ||
        !isSingleGenStatusFilter ||
        generatorStatusFilterValue !== selectedChartGeneratorStatus
      ) {
        setSelectedChartGeneratorStatus(null);
      }
    }
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [generatorStatusFilterValue, selectedChartGeneratorStatus]);

  // Simple static title - filter context is shown in the Status dropdown
  const titleFilter = "Submissions";

  // Custom empty state overlay for the grid
  const CustomNoRowsOverlay = useCallback(
    () => (
      <SubmissionsEmptyState
        timeFilter={timeFilterForEmptyState}
        readyForAction={readyForAction}
        isStaff={isStaff}
        onCreateSubmission={handleAddSubmission}
        onViewUpcoming={handleViewUpcoming}
        omnisearch={omnisearch}
        onClearSearch={clearOmnisearch}
      />
    ),
    [
      timeFilterForEmptyState,
      readyForAction,
      isStaff,
      handleAddSubmission,
      handleViewUpcoming,
      omnisearch,
      clearOmnisearch,
    ]
  );

  // Memoize slots to prevent new object on every render
  // Must be after CustomNoRowsOverlay is defined
  const slots = useMemo(() => ({ noRowsOverlay: CustomNoRowsOverlay }), [CustomNoRowsOverlay]);

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Submission Info",
        fields: ["submissionStatus", "submissionType", "dueDate", "documents"],
      },
      {
        name: "Location",
        fields: ["facilityId", "states", "generatorStatus", "epaId", "customerFacilityId"],
      },
      {
        name: "Tags",
        fields: ["facilityTags"],
      },
      {
        name: "Assignment",
        fields: ["reviewers", "signer", ...(isStaff ? ["staffAssignee"] : []), "waitingOn"],
      },
    ],
    [isStaff]
  );

  return (
    <Stack sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        id="submission-filter-header"
        paddingX={2}
        paddingY={1.5}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1.5}
          flexWrap="wrap"
        >
          <Stack direction="row" gap={1.5} alignItems="flex-end" flexWrap="wrap">
            {/* Status Filter */}
            <StatusFilter
              committedStatuses={committedStatuses}
              defaultStatuses={defaultStatusesFromAll}
              onCommit={handleStatusFilterCommit}
            />

            {/* View By Filter */}
            <FormControl variant="standard" size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="view-by-label">View by</InputLabel>
              <Select<ViewByValue>
                labelId="view-by-label"
                value={viewByValue}
                onChange={handleViewByChange}
                renderValue={(v) => {
                  switch (v) {
                    case "readyForAction":
                      return "Ready for next action";
                    case "needsMyAttention":
                      return "Needs my attention";
                    case "peerReviewQueue":
                      return "Peer review queue";
                    default:
                      return "All";
                  }
                }}
              >
                <MenuItem value="all">
                  <Radio checked={viewByValue === "all"} size="small" sx={{ p: 0, mr: 1 }} />
                  All
                </MenuItem>
                <MenuItem value="readyForAction">
                  <Radio
                    checked={viewByValue === "readyForAction"}
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                  />
                  Ready for next action
                </MenuItem>
                {isStaff && (
                  <MenuItem value="needsMyAttention">
                    <Radio
                      checked={viewByValue === "needsMyAttention"}
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    Needs my attention
                  </MenuItem>
                )}
                {isStaff && (
                  <MenuItem value="peerReviewQueue">
                    <Radio
                      checked={viewByValue === "peerReviewQueue"}
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    Peer review queue
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Generator Status Filter */}
            <GeneratorStatusFilter
              committedGeneratorStatuses={committedGeneratorStatuses}
              onCommit={handleGeneratorStatusFilterCommit}
            />

            {/* State Filter */}
            <StateFilter committedStates={committedStates} onCommit={handleStateFilterCommit} />

            {/* Due Date Range Filter */}
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker
                value={[dueDateStart, dueDateEnd]}
                onChange={handleDueDateRangeChange}
                localeText={{ start: "Due Start", end: "Due End" }}
                slotProps={{
                  shortcuts: {
                    items: datePickerShortcuts,
                  },
                  textField: {
                    variant: "standard",
                    size: "small",
                    sx: { width: 105 },
                    inputProps: {
                      autoComplete: "off",
                      "data-lpignore": "true",
                      "data-form-type": "other",
                      "data-1p-ignore": "true",
                    },
                  },
                }}
                slots={{
                  field: MultiInputDateRangeField,
                }}
              />
            </LocalizationProvider>

            {/* Tags Filter */}
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              sx={{ alignSelf: "flex-end", mb: 0.5 }}
            >
              <TagsFilter
                allFacilityTags={allFacilityTags}
                committedFacilityTagNames={committedFacilityTagNames}
                isFacilityTagsNull={isFacilityTagsNull}
                isFetchingMoreTags={isFetchingMoreFacilityTags}
                onCommit={handleTagsFilterCommit}
                onScroll={handleFacilityTagsScroll}
              />
              <Button
                onClick={handleResetFilters}
                variant="text"
                size="small"
                sx={{
                  visibility: areFiltersAtDefault ? "hidden" : "visible",
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
          <Stack direction="row" gap={0.5} alignItems="center">
            <Button onClick={handleToggle} variant="text" size="small">
              {statsOpen ? "Hide Stats" : "Show Stats"}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <StatsSection
        statsOpen={statsOpen}
        onStatusClick={handleChartStatusClick}
        selectedStatus={selectedChartStatus}
        onStateClick={handleChartStateClick}
        selectedState={selectedChartState}
        onGeneratorStatusClick={handleChartGeneratorStatusClick}
        selectedGeneratorStatus={selectedChartGeneratorStatus}
      />
      <Stack
        id="submission-list"
        p={2}
        sx={{
          background: theme.palette.grey50.main,
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <GridSection
          title={titleFilter}
          description="General waste reporting submissions. Use the search and filters to find specific submissions."
          slots={slots}
          persistenceKey={omnisearchPersistenceKey}
          dataQuery={SUBMISSION_VIRTUAL_EVENTS_QUERY}
          defaultSearch={defaultSearch}
          getItems={getItems}
          getCount={getCount}
          checkboxSelection={isStaff}
          selectedIds={isStaff ? selectedIds : undefined}
          onSelectedRowsChanged={isStaff ? handleSelectedRowsChanged : undefined}
          onSelectedIdsChanged={isStaff ? handleSelectedIdsChanged : undefined}
          onRowClick={handleGridRowClick}
          getRowLink={getRowHref}
          commandButtons={commandButtons}
          columnVisibilityModel={columnVisibilityModel}
          filterGroups={filterGroups}
          tenantId={tenantId}
        />
      </Stack>

      <BulkCreateNewSubmissionsDialog
        open={showBulkCreateDialog}
        onClose={() => setShowBulkCreateDialog(false)}
        onSuccess={handleBulkCreateSuccess}
      />

      {/* Mounted only while open so the flow/title/facility/due-date state
          resets on every close path (cancel, continue-legacy, successful
          create) rather than persisting into the next open. */}
      {showTypeDialog && (
        <CreateSubmissionTypeDialog
          open={showTypeDialog}
          onClose={() => setShowTypeDialog(false)}
          onContinueLegacy={handleContinueLegacy}
          onCreateStepper={handleCreateStepper}
          creating={creatingStepper}
        />
      )}
    </Stack>
  );
}
